import './AmcTouchpointPieChart.scss';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Cell, Pie, PieChart, PieLabelRenderProps, Sector, Tooltip } from 'recharts';
import { toPrettyNumber } from '@src/utils/performance-utils';
import { ChartTooltip } from '@Components/Charts/ChartTooltip/ChartTooltip';

export interface AmcTouchpointSlice {
  adType: string;
  label: string;
  value: number;
  fillColor: string;
}

interface AmcTouchpointPieChartProps {
  data: AmcTouchpointSlice[];
}

const RADIAN = Math.PI / 180;
// Only label slices big enough for the percentage text to fit legibly.
const MIN_LABELED_SLICE_PERCENT = 0.05;

const renderPercentLabel = ({
  cx = 0,
  cy = 0,
  midAngle = 0,
  innerRadius = 0,
  outerRadius = 0,
  percent = 0
}: PieLabelRenderProps) => {
  if (percent <= MIN_LABELED_SLICE_PERCENT) return null;
  const radius = Number(innerRadius) + (Number(outerRadius) - Number(innerRadius)) * 0.65;
  const x = Number(cx) + radius * Math.cos(-midAngle * RADIAN);
  const y = Number(cy) + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      textAnchor='middle'
      dominantBaseline='central'
      className='amc-touchpoint-percent-label'
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

interface ActiveShapeProps {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill?: string;
}

// Draws a translucent ring just outside the hovered slice, matching bpm-frontend's hover treatment.
const renderActiveShape = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill = '#8884d8'
}: ActiveShapeProps) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
    <Sector
      cx={cx}
      cy={cy}
      startAngle={startAngle}
      endAngle={endAngle}
      innerRadius={outerRadius}
      outerRadius={outerRadius + 8}
      fill={`${fill}4d`}
    />
  </g>
);

interface AmcTouchpointPieChartTooltipProps {
  active?: boolean;
  payload?: { payload: AmcTouchpointSlice }[];
  total: number;
}

const AmcTouchpointPieChartTooltip = ({
  active,
  payload,
  total
}: AmcTouchpointPieChartTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;
  const { payload: slice } = payload[0];
  const percent = total > 0 ? (slice.value / total) * 100 : 0;
  return (
    <ChartTooltip
      headerLabel={slice.label}
      items={[
        {
          label: 'Conversions',
          value: `${toPrettyNumber(slice.value)} (${percent.toFixed(1)}%)`,
          color: slice.fillColor
        }
      ]}
    />
  );
};

export const AmcTouchpointPieChart = ({ data }: AmcTouchpointPieChartProps) => {
  const slices = data.filter(slice => slice.value > 0);
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  return (
    <div className='amc-touchpoint-pie-chart'>
      <AutoSizer
        renderProp={({ width = 0, height = 0 }) => (
          <PieChart width={width} height={height}>
            <Tooltip content={<AmcTouchpointPieChartTooltip total={total} />} />
            <Pie
              data={slices}
              dataKey='value'
              nameKey='adType'
              cx='50%'
              cy='50%'
              outerRadius={Math.min(width, height) / 2 - 10}
              startAngle={-270}
              labelLine={false}
              label={renderPercentLabel}
              activeShape={renderActiveShape}
              isAnimationActive={false}
            >
              {slices.map(slice => (
                <Cell key={slice.adType} fill={slice.fillColor} />
              ))}
            </Pie>
          </PieChart>
        )}
      />
    </div>
  );
};
